import type { Client } from "@/api/schemas";

/** Mismo criterio que la paleta de comandos: sin tildes ni mayúsculas. */
export function normalizar(value: string): string {
  return value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

export function filtrarClientes(clients: readonly Client[], query: string): Client[] {
  const needle = normalizar(query.trim());
  if (!needle) return [...clients];
  return clients.filter((c) => normalizar(c.name).includes(needle) || normalizar(c.slug).includes(needle));
}

/** El cliente sale del slug de la URL y de nada más (ni `business_id`, ni almacenamiento). */
export function resolverPorSlug(clients: readonly Client[], slug: string | undefined): Client | undefined {
  return slug ? clients.find((c) => c.slug === slug) : undefined;
}

interface PreferenciasDeInicio {
  /** `business_id` que traía una ruta antigua (`?business_id=`). */
  pedido: string | null;
  /** Slugs usados, el más reciente primero (`ads:{usuario}:recientes`). */
  recientes: readonly string[];
}

/** Adónde manda `/` y cada ruta antigua: el pedido si hay acceso, el último usado o el primero. */
export function clienteInicial(clients: readonly Client[], { pedido, recientes }: PreferenciasDeInicio): Client | undefined {
  const activos = clients.filter((c) => c.active);
  const porPedido = pedido ? activos.find((c) => c.business_id === pedido) : undefined;
  if (porPedido) return porPedido;
  for (const slug of recientes) {
    const reciente = activos.find((c) => c.slug === slug);
    if (reciente) return reciente;
  }
  return activos[0];
}

const RECIENTES_VISIBLES = 3;
const CLIENTES_PARA_RECIENTES = 4;
const collator = new Intl.Collator("es", { sensitivity: "base" });

export interface ListaDelSelector {
  recientes: Client[];
  todos: Client[];
}

/** Grupos «Recientes» (los 3 últimos sin el actual) y «Todos» (alfabético) — interaction.md §2.2. */
export function ordenarParaSelector(clients: readonly Client[], { actualSlug, recientes }: { actualSlug: string | null; recientes: readonly string[] }): ListaDelSelector {
  const todos = [...clients].sort((a, b) => collator.compare(a.name, b.name));
  if (clients.length < CLIENTES_PARA_RECIENTES) return { recientes: [], todos };
  const vistos = new Set<string>();
  const ultimos: Client[] = [];
  for (const slug of recientes) {
    const cliente = clients.find((c) => c.slug === slug);
    if (!cliente || slug === actualSlug || vistos.has(slug)) continue;
    vistos.add(slug);
    ultimos.push(cliente);
  }
  return { recientes: ultimos.slice(0, RECIENTES_VISIBLES), todos };
}

export function iniciales(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  return words.slice(0, 2).map((w) => Array.from(w)[0]?.toLocaleUpperCase("es") ?? "").join("");
}
